<template>
  <v-card width="800px" class="ma-8">
    <v-toolbar flat color="primary" dark>
      <v-toolbar-title>New Context</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon :to="{ name: 'dashboard' }"><v-icon>close</v-icon></v-btn>
    </v-toolbar>
    <v-card-text class="pt-0">
      <choose-template
        v-if="step == 0"
        :guides="guides"
        @selectTemplate="
          templateId = $event;
          step = 1;
        "
      ></choose-template>
      <guide-wrapper
        v-else-if="step == 1"
        :guide="guides[templateId]"
        @back="step = 0"
      ></guide-wrapper>
    </v-card-text>
  </v-card>
</template>

<script>
import ChooseTemplate from './ChooseTemplate';
import GuideWrapper from './GuideWrapper';
import { mapState, mapMutations, mapActions } from 'vuex';

//  import store to work with beforeRouteEnter
import store from '@/store';

export default {
  beforeRouteEnter(to, from, next) {
    // check that workspace id is already setup
    try {
      const setup = store.state.views.newContext.setup;
      if (!setup.workspaceId) {
        next({ name: 'dashboard' });
      } else {
        next();
      }
    } catch (e) {
      console.log(e);
      next({ name: 'dashboard' });
    }
  },
  components: { ChooseTemplate, GuideWrapper },
  computed: {
    ...mapState('views/newContext', ['setup', 'newContextId'])
  },
  methods: {
    ...mapActions('context', ['createNewContext', 'subscribeToContext']),
    ...mapMutations('views/newContext', ['SET_NEWCONTEXT_STATE', 'clearState'])
  },
  mounted() {
    if (!this.newContextId) {
      //  create a new empty context
      console.log('create context')
      const { parentFolderId, workspaceId } = this.setup;
      this.createNewContext({ parentFolderId, workspaceId }).then(id => {
        console.log('context created', id);
        this.SET_NEWCONTEXT_STATE({ key: 'newContextId', value: id });
        this.subscribeToContext({ id });
      });
    }
  },
  destroyed() {
    this.clearState();
  },
  data() {
    return {
      step: 1,
      templateId: 1,
      concerns: [],
      guides: {
        0: {
          id: 0,
          title: 'Project Decision',
          description:
            'Understand if you should spend time and energy on a particular project or effort.',
          example: 'Should we rebuild our website?',
          steps: [
            {
              title: 'What is the question you are considering',
              placeholder: 'Should we...',
              examples: [
                'Should we invest more in online marketing?',
                'Should we build a mobile site?'
              ]
            },
            {
              title: `What's important to consider?`,
              description: `What concerns or key questions will factor into your decision? It's ok if they are vague, you can always refine them or add more later.`,
              placeholder: 'My concern... (press enter to add)',
              tip:
                'If you are concerned it will take too many resources, a good question is "will it take too many resources?',
              suggestions: [
                //  Why, What, How
                {
                  text: 'Is there potential for a real positive impact?',
                  questions: [],
                  qSuggestions: [
                    `How will it impact the bottom line?`,
                    'Would it help the company be more productive?',
                    'What do we expect to gain?',
                    `What could we lose?`,
                    `What assumptions need to be true to have a positive impact?`,
                    `What milestones are appropriate?`,
                    `How and when do we know if it's successful?`,
                    `How and when do we know if it's not successful?`,
                    `Would it improve the work environment for employees?`
                  ]
                },
                {
                  text: 'Does it align with our goals?',
                  questions: [],
                  qSuggestions: [
                    `How will this project help us reach our goals?`,
                    `How might this project distract us from our goals?`
                  ]
                },
                {
                  text: 'Do people want to use it?',
                  questions: [],
                  qSuggestions: [
                    `Who would use it?`,
                    `Why would people use it?`,
                    `What would stop people from using it?`
                  ]
                },
                {
                  text: 'Will it cost too much?',
                  questions: [],
                  qSuggestions: [
                    `How much have similar projects cost?`,
                    `What do experts estimate it will cost?`,
                    'What does our team think it will cost?',
                    `If everything goes terribly, what might it cost?`,
                    `It everything goes great, how much might it cost?`
                  ]
                },
                {
                  text: 'Will it take too long?',
                  questions: [],
                  qSuggestions: [
                    `How long have similar projects taken?`,
                    `How long do experts estimate it will take?`,
                    'How long does our team think it will take?',
                    `If everything goes terribly, how long might it take?`,
                    `It everything goes great, how long might it take?`
                  ]
                },
                {
                  text: 'Do we have the resources to do it?',
                  questions: [],
                  qSuggestions: [
                    `Who is going to do it?`,
                    `Who is going to make sure it gets done?`,
                    `What else could we do with these resources?`
                  ]
                },
                {
                  text: 'Are there potential bottlenecks?',
                  questions: [],
                  qSuggestions: [
                    `What has derailed projects like this in the past?`,
                    `Where are we tight on required resources?`
                  ]
                }
              ]
            },
            {
              title: `Now let's add what you need to know`,
              description: `For each concern, add questions you'll need to find answers to. These questions will guide your research and activity.`,
              placeholder: 'My question to research... (press enter to add)',
              tip: `Think critically. Ask both what would make the answer more likey yes as well as what would make it no.`,
              example: [
                `How much have similar projects cost?`,
                'How long does the software team think it will take?'
              ]
            }
            // {
            //   title: `Now let's add some intial thoughts`,
            //   description: `For each concern, add a few thoughts or assumptions to guide research and help frame the question.`,
            //   placeholder: 'My thought... (press enter to add)',
            //   tip: `At this stage assume no one knows anything for sure, so put down thoughts like "I don't think this should cost more than Y" or "It's possible this may take too long."`
            // }
          ]
        },
        1: {
          id: 1,
          title: "Blank Context",
          description: 'An empty context so you can set it up however you wish.',
          steps: [
            {
              title: 'What do you want to make a Context on?',
              placeholder: 'My topic...',
              examples: [
                'Should we invest more in online marketing?',
                'How much is too much?',
                'Why is the sky blue?'
              ]
            }
          ]
        },
        // 2: {
        //   id: 1,
        //   title: 'Knowledge Question',
        //   description: 'Organize the state of knowledge around a key question.',
        //   example: 'What is our biggest competition?',
        //   steps: [
        //     {
        //       text: 'What topic do you want to capture knowledge around?',
        //       placeholder: 'What is ...',
        //       examples: [
        //         'What is our competition doing well?',
        //         'What are our key opportunities this year?',
        //         'What are the top reasons people use our product?'
        //       ]
        //     },
        //     {
        //       text: `What's important to consider?`,
        //       description: `What concerns or key questions will factor into your decision? It's ok if they are vague, you can always refine them or add more later.`,
        //       placeholder: 'My concern... (press enter to add)',
        //       tip:
        //         'If you are concerned it will take too many resources, a good question is "will it take too many resources?',
        //       suggestions: [
        //         'Will it cost too much?',
        //         'Will it take too long?',
        //         'Will it impact the bottom line?',
        //         'Does it align with our goals?',
        //         'Do people want or use it?',
        //         'Do we have the resources to do it',
        //         'Are there potential bottlenecks?'
        //       ]
        //     },
        //     {
        //       text: `Now let's add some intial thoughts`,
        //       description: `For each concern, add a few thoughts or assumptions to guide research and help frame the question.`,
        //       placeholder: 'My thought... (press enter to add)',
        //       tip: `At this stage assume no one knows anything for sure, so put down thoughts like "I don't think this should cost more than Y" or "It's possible this may take too long."`
        //     }
        //   ]
        // },
        // 2: {
        //   id: 2,
        //   title: 'Strategy Decision',
        //   description: 'Explore a complex topic with many possibel outcomes.',
        //   example: 'What should our Q1 sales target be?'
        // }
      }
    };
  }
};
</script>

<style></style>
